import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RECOMMENDATION_SLOT_CODES, resolveRecommendationSlot } from '../../api/common';
import type { RecommendationItem } from '../../api/common';
import './blogPanel.scss';
import { trackBlogReadClick, trackBlogSeeMoreClick, trackLeftsideEntryExposure, trackBilibiliVideosClick, trackVideoSwitchClick, trackBilibiliTopVideosClick } from '../../hooks/useMatomoTrackingPoint';

/* ---- 类型定义 ---- */

export interface VideoItem {
    /** B 站播放器 URL（externalUrl） */
    url: string;
    title: string;
    /** 仅控制播放按钮和时长的显示，不参与 Slot 分流 */
    isVideo: boolean;
    /** 当前播放进度，如 "00:09" */
    currentTime?: string;
    /** 总时长，如 "02:59" */
    duration: string;
    /** 封面图 URL */
    coverUrl?: string;
    tags?: string[];
}

export interface BlogItem {
    title: string;
    url: string;
    coverUrl?: string;
    tags: string[];
    /** 后端返回的原图宽高，用于按比例预占位和瀑布流排版 */
    width?: number;
    height?: number;
}

export interface BlogPanelData {
    sectionTitle: string;
    featuredVideo: VideoItem;
    singleVideo: VideoItem | null;
    videoList: VideoItem[];
    latestSectionTitle: string;
    blogItems: BlogItem[];
}

export interface BlogPanelProps {
    onMoreClick?: () => void;
    onVideoSelect?: (video: VideoItem) => void;
}

/* ---- 辅助函数 ---- */

/** 将秒数格式化为 MM:SS */
function formatDuration(seconds?: number): string {
    if (seconds === undefined || seconds === null || seconds < 0) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function isSameOriginPromptUrl(value: string): boolean {
    try {
        const url = new URL(value, window.location.origin);
        return url.origin === window.location.origin && (url.pathname === '/prompt' || url.pathname.startsWith('/prompt/'));
    } catch {
        return false;
    }
}

const OPEN_PROMPT_FRAME_EVENT = 'krene:open-prompt-frame';

function openRecommendationUrl(value: string): void {
    if (!value) return;

    if (isSameOriginPromptUrl(value)) {
        window.dispatchEvent(new CustomEvent(OPEN_PROMPT_FRAME_EVENT, { detail: { url: value } }));
        return;
    }

    window.open(value, '_blank', 'noopener,noreferrer');
}

interface HomepageRecommendationSlots {
    autoplay: RecommendationItem[];
    top: RecommendationItem[];
    carousel: RecommendationItem[];
    blogFeed: RecommendationItem[];
}

function toVideoItem(item: RecommendationItem): VideoItem {
    return {
        url: item.externalUrl,
        title: item.title,
        isVideo: item.type !== 'blog',
        duration: formatDuration(item.durationSeconds),
        coverUrl: item.coverUrl || item.previewImageUrl,
        tags: item.tags ?? [],
    };
}

function toBlogItem(item: RecommendationItem): BlogItem {
    return {
        title: item.title,
        url: item.externalUrl,
        coverUrl: item.coverUrl || item.previewImageUrl,
        tags: item.tags ?? [],
        width: item.width,
        height: item.height,
    };
}

/** Slot 决定展示位置；内容 type 只作为素材属性，不再用来二次分流。 */
function mapRecommendationSlotsToPanelData(slots: HomepageRecommendationSlots): BlogPanelData {
    const featuredVideo = slots.autoplay[0];
    const topItem = slots.top[0];

    return {
        sectionTitle: '原创视频教程',
        featuredVideo: featuredVideo ? toVideoItem(featuredVideo) : {
            url: '',
            title: '暂无视频',
            isVideo: true,
            duration: '00:00',
        },
        singleVideo: topItem ? toVideoItem(topItem) : null,
        videoList: slots.carousel.map(toVideoItem),
        latestSectionTitle: '原创图文教程',
        blogItems: slots.blogFeed.map(toBlogItem),
    };
}

/* ---- 宽高驱动的瀑布流排版 ---- */

const WATERFALL_GAP = 6;

interface PanelMetrics {
    leftColCount: number;
    rightColCount: number;
    leftColWidth: number;
    rightColWidth: number;
}

/** 与 blogPanel.scss 断点保持一致，由窗口宽度推导瀑布流的列数与列宽 */
function getPanelMetrics(winWidth: number): PanelMetrics {
    let panelW: number;
    let leftRatio: number;
    let leftColCount: number;
    let rightColCount: number;

    if (winWidth >= 1820) {
        panelW = 1580;
        leftRatio = 3 / 5;
        leftColCount = 3;
        rightColCount = 2;
    } else if (winWidth >= 1720) {
        panelW = 1480;
        leftRatio = 4 / 5;
        leftColCount = 3;
        rightColCount = 1;
    } else if (winWidth >= 1120) {
        panelW = 908;
        leftRatio = 1;
        leftColCount = 3;
        rightColCount = 0;
    } else if (winWidth >= 769) {
        panelW = 0.75 * winWidth;
        leftRatio = 1;
        leftColCount = 2;
        rightColCount = 0;
    } else {
        panelW = 0.96 * winWidth;
        leftRatio = 1;
        leftColCount = 2;
        rightColCount = 0;
    }

    const leftW = (panelW - WATERFALL_GAP) * leftRatio;
    const rightW = (panelW - WATERFALL_GAP) * (1 - leftRatio);
    const leftColWidth = leftColCount > 0 ? (leftW - (leftColCount - 1) * WATERFALL_GAP) / leftColCount : 0;
    const rightColWidth = rightColCount > 0 ? (rightW - (rightColCount - 1) * WATERFALL_GAP) / rightColCount : 0;
    return { leftColCount, rightColCount, leftColWidth, rightColWidth };
}

interface BlogMasonryColumn {
    items: BlogItem[];
    height: number;
}

/**
 * 根据后端返回的宽高，把图文分到“当前最短”的列（取代旧的固定比例 splitIdx）。
 * 左列顶部有视频区块，用其高度做初始偏移，保证左右两区底部尽量对齐。
 */
function buildMasonryColumns(
    items: BlogItem[],
    metrics: PanelMetrics,
    videoBlockHeight: number,
): { left: BlogMasonryColumn[]; right: BlogMasonryColumn[] } {
    const { leftColCount, rightColCount, leftColWidth, rightColWidth } = metrics;
    const columns: BlogMasonryColumn[] = [];

    for (let i = 0; i < leftColCount; i++) {
        columns.push({ items: [], height: videoBlockHeight + WATERFALL_GAP });
    }
    for (let i = 0; i < rightColCount; i++) {
        columns.push({ items: [], height: 0 });
    }

    if (columns.length === 0) return { left: [], right: [] };

    items.forEach((item) => {
        const w = item.width && item.width > 0 ? item.width : 1;
        const h = item.height && item.height > 0 ? item.height : 1;

        let bestIdx = 0;
        let bestEnd = Infinity;
        for (let i = 0; i < columns.length; i++) {
            const colW = i < leftColCount ? leftColWidth : rightColWidth;
            const itemH = colW > 0 ? colW * (h / w) + WATERFALL_GAP : 1;
            const end = columns[i].height + itemH;
            if (end < bestEnd) {
                bestEnd = end;
                bestIdx = i;
            }
        }

        columns[bestIdx].items.push(item);
        const colW = bestIdx < leftColCount ? leftColWidth : rightColWidth;
        columns[bestIdx].height += colW > 0 ? colW * (h / w) + WATERFALL_GAP : 1;
    });

    return {
        left: columns.slice(0, leftColCount),
        right: columns.slice(leftColCount),
    };
}

/** 单篇图文卡片：按后端宽高预占位，图片加载完成前显示灰色底 */
function BlogCard({ item }: { item: BlogItem }) {
    const [loaded, setLoaded] = useState(false);

    // 已缓存的图片可能在 onLoad 绑定前就触发 load，这里兜底一次
    const handleImgRef = useCallback((el: HTMLImageElement | null) => {
        if (el && el.complete && el.naturalWidth > 0) {
            setLoaded(true);
        }
    }, []);

    return (
        <div
            className={`bp-blog-item${loaded ? '' : ' bp-blog-item--loading'}`}
            style={{
                aspectRatio: item.width && item.height ? `${item.width} / ${item.height}` : undefined,
                cursor: 'pointer',
            }}
            onClick={() => {
                if (!item.url) return;
                trackBlogReadClick({ page_id: window.location.href, message_id: item.url });
                openRecommendationUrl(item.url);
            }}
        >
            <img
                ref={handleImgRef}
                className={`bp-blog-img${loaded ? ' bp-blog-img--loaded' : ''}`}
                src={item.coverUrl}
                alt={item.title}
                loading="lazy"
                onLoad={() => setLoaded(true)}
            />
        </div>
    );
}

/* ---- 组件 ---- */

const TAG_COLORS = ['#F3667C', 'var(--c-brand-light)', '#D386ED'];

export default function HomeBlogPanel({ onMoreClick, onVideoSelect }: BlogPanelProps) {
    // ---------- 曝光埋点（ref 防止 StrictMode 二次触发） ----------
    const exposureSent = useRef(false);
    useEffect(() => {
        if (exposureSent.current) return;
        exposureSent.current = true;
        trackLeftsideEntryExposure({ page_id: window.location.href });
    }, []);

    // ---------- 数据获取 ----------

    const [apiData, setApiData] = useState<BlogPanelData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            resolveRecommendationSlot(RECOMMENDATION_SLOT_CODES.HOME_AUTOPLAY, []),
            resolveRecommendationSlot(RECOMMENDATION_SLOT_CODES.HOME_TOP, []),
            resolveRecommendationSlot(RECOMMENDATION_SLOT_CODES.HOME_CAROUSEL, []),
            resolveRecommendationSlot(RECOMMENDATION_SLOT_CODES.HOME_BLOG_FEED, []),
        ])
            .then(([autoplay, top, carousel, blogFeed]) => {
                if (cancelled) return;
                const hasItems = autoplay.length + top.length + carousel.length + blogFeed.length > 0;
                setApiData(hasItems ? mapRecommendationSlotsToPanelData({ autoplay, top, carousel, blogFeed }) : null);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setApiData(null);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const resolved = apiData;

    // ---------- 轮播 hooks ----------

    const itemCount = resolved?.videoList?.length ?? 0;
    const [activeIdx, setActiveIdx] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval>>();

    const restartTimer = useCallback(() => {
        clearInterval(timerRef.current);
        if (itemCount <= 1) return;
        timerRef.current = setInterval(() => {
            setActiveIdx((prev) => (prev + 1) % itemCount);
        }, 7000);
    }, [itemCount]);

    useEffect(() => {
        restartTimer();
        return () => clearInterval(timerRef.current);
    }, [restartTimer]);

    const goTo = useCallback(
        (idx: number) => {
            setActiveIdx(idx);
            restartTimer();
        },
        [restartTimer],
    );

    const goPrev = useCallback(() => {
        goTo((activeIdx - 1 + itemCount) % itemCount);
    }, [activeIdx, itemCount, goTo]);

    const goNext = useCallback(() => {
        goTo((activeIdx + 1) % itemCount);
    }, [activeIdx, itemCount, goTo]);

    const handleDotClick = useCallback(
        (e: React.MouseEvent, idx: number) => {
            e.stopPropagation();
            goTo(idx);
        },
        [goTo],
    );

    const handleArrowClick = useCallback(
        (e: React.MouseEvent, dir: 'prev' | 'next') => {
            e.stopPropagation();
            dir === 'prev' ? goPrev() : goNext();
            trackVideoSwitchClick({ page_id: window.location.href, switch_from: dir === 'prev' ? 'left' : 'right' });
        },
        [goPrev, goNext],
    );

    const handleVideoClick = useCallback(
        (video: VideoItem, idx: number) => {
            onVideoSelect?.(video);
            goTo(idx);
            trackBilibiliVideosClick({ page_id: window.location.href, message_id: video.url });
            openRecommendationUrl(video.url);
        },
        [onVideoSelect, goTo],
    );

    // ---------- 响应式窗口宽度 ----------

    const [winWidth, setWinWidth] = useState(window.innerWidth);
    useEffect(() => {
        const onResize = () => setWinWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // ---------- 宽高驱动排版（取代旧的固定比例左右分配） ----------

    const hasVideo = !!resolved && (!!resolved.singleVideo || (resolved.videoList?.length ?? 0) > 0);

    // 视频区块高度：左列瀑布流从视频下方开始，用它做左右两区的底部对齐
    const videoBlockRef = useRef<HTMLDivElement | null>(null);
    const [videoBlockHeight, setVideoBlockHeight] = useState(0);
    useEffect(() => {
        const el = videoBlockRef.current;
        if (!el) {
            setVideoBlockHeight(0);
            return;
        }
        const update = () => setVideoBlockHeight(el.offsetHeight);
        update();
        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(update);
            ro.observe(el);
            return () => ro.disconnect();
        }
        return undefined;
    }, [hasVideo]);

    // 奇偶交错排序：使瀑布流相邻条目不在同一列
    const blogItemsForLayout = resolved?.blogItems ?? [];
    const reorderedBlogItems = [
        ...blogItemsForLayout.filter((_, i) => i % 2 === 0),
        ...blogItemsForLayout.filter((_, i) => i % 2 === 1),
    ];

    // 按后端宽高做“最短列”贪心排版
    const { left: leftCols, right: rightCols } = buildMasonryColumns(
        reorderedBlogItems,
        getPanelMetrics(winWidth),
        videoBlockHeight,
    );

    // ---------- 加载态 ----------

    if (loading) {
        return <></>
    }

    // ---------- 空数据 ----------

    if (!resolved) {
        return <></>
    }

    const { featuredVideo, singleVideo, videoList } = resolved;

    return (
        <div className="bp-scroll-wrap">
            <div className="blog-panel">
            <div className="bp-grid">
                {/* ===== 左区（3/5 宽度） ===== */}
                <div className="bp-left">
                    {/* 视频卡片 */}
                    {hasVideo && (
                        <div className="bp-video-cards" ref={videoBlockRef}>
                            {singleVideo && (
                                <div
                                    className="bp-card bp-card--carousel"
                                    onClick={() => {
                                        trackBilibiliTopVideosClick({ page_id: window.location.href, message_id: singleVideo.url });
                                        openRecommendationUrl(singleVideo.url);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="bp-video-box">
                                        {singleVideo.coverUrl ? (
                                            <div className="bp-video-thumb" style={{ backgroundImage: `url("${singleVideo.coverUrl}")` }} />
                                        ) : (
                                            <div className="bp-video-thumb bp-video-thumb--pick" />
                                        )}
                                        {singleVideo.tags && singleVideo.tags.length > 0 && (
                                            <div className="bp-video-tags bp-video-tags--top">
                                                {singleVideo.tags.map((tag, i) => (
                                                    <span
                                                        key={i}
                                                        className="bp-video-tag"
                                                        style={{ backgroundColor: TAG_COLORS[i % TAG_COLORS.length] }}
                                                    >
                    {tag}
                  </span>
                                                ))}
                                            </div>
                                        )}
                                        {singleVideo.isVideo && <div className="bp-play-icon" />}
                                        {singleVideo.isVideo && <div className="bp-time-tag">{singleVideo.duration}</div>}
                                    </div>
                                    <div className="bp-video-text">
                                        <span className="bp-video-title">{singleVideo.title}</span>
                                    </div>
                                </div>
                            )}

                            {itemCount > 0 &&
                                (() => {
                                    const video = videoList[activeIdx];
                                    return (
                                        <div
                                            className="bp-card bp-card--carousel"
                                            onClick={() => handleVideoClick(video, activeIdx)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="bp-video-box">
                                                {video.coverUrl ? (
                                                    <div className="bp-video-thumb" style={{ backgroundImage: `url("${video.coverUrl}")` }} />
                                                ) : (
                                                    <div className="bp-video-thumb bp-video-thumb--pick" />
                                                )}
                                                {video.tags && video.tags.length > 0 && (
                                                    <div className="bp-video-tags bp-video-tags--top">
                                                        {video.tags.map((tag, i) => (
                                                            <span
                                                                key={i}
                                                                className="bp-video-tag"
                                                                style={{ backgroundColor: TAG_COLORS[i % TAG_COLORS.length] }}
                                                            >
                        {tag}
                      </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {video.isVideo && <div className="bp-play-icon" />}
                                                {video.isVideo && <div className="bp-time-tag">{video.duration}</div>}
                                            </div>
                                            <div className="bp-video-text bp-video-text--with-picker">
                                                <span className="bp-video-title">{video.title}</span>
                                                {itemCount > 1 && (
                                                    <div className="bp-picker">
                                                        {Array.from({ length: itemCount }, (_, dotIdx) =>
                                                            dotIdx === activeIdx ? (
                                                                <span key={dotIdx} className="bp-bar" />
                                                            ) : (
                                                                <span key={dotIdx} className="bp-dot" onClick={(e) => handleDotClick(e, dotIdx)} />
                                                            ),
                                                        )}
                                                        <span className="bp-picker-spacer" />
                                                        <span className="bp-arrow bp-arrow--left" onClick={(e) => handleArrowClick(e, 'prev')} />
                                                        <span className="bp-arrow bp-arrow--right" onClick={(e) => handleArrowClick(e, 'next')} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}
                        </div>
                    )}

                    {/* 左区 3 列瀑布流（按宽高最短列分配） */}
                    {leftCols.some((col) => col.items.length > 0) && (
                        <div className="bp-left-waterfall">
                            {leftCols.map((col, ci) => (
                                <div className="bp-col" key={ci}>
                                    {col.items.map((item, i) => (
                                        <BlogCard key={item.url || i} item={item} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ===== 右区（2/5 宽度）瀑布流（按宽高最短列分配） ===== */}
                {rightCols.some((col) => col.items.length > 0) && (
                    <div className="bp-right">
                        <div className="bp-right-waterfall">
                            {rightCols.map((col, ci) => (
                                <div className="bp-col" key={ci}>
                                    {col.items.map((item, i) => (
                                        <BlogCard key={item.url || i} item={item} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            </div>
        </div>
    );
}
